import * as React from 'react';
import { Dispatch } from 'react-redux';

import { Wrapper, Inner, Icon } from './styles';
import { StoreState } from '../../../../../reducers';
import Utils from '../../../../../utils';
import { toggleThumbnails, setThumbnailsWidth } from '../../../../../containers/Editor/actions';
import { INITIAL_WIDTH, MIN_WIDTH, WIDTH_TO_HIDE } from '../../../../../containers/Editor/reducer/thumbnails';

interface ToggleProps {
	isOpened: boolean;
	listElement: React.MutableRefObject<HTMLDivElement | null>;
	dispatch: Dispatch<StoreState>;
}

interface ResizeData {
	isResizing: boolean;
	startX: number;
	initialWidth: number;
	diff: number;
}

const Toggle = ({ isOpened, listElement, dispatch }: ToggleProps) => {
	const toggleElement = React.useRef<HTMLButtonElement | null>(null);
	const resizeData = React.useRef<ResizeData>({
		isResizing: false,
		startX: 0,
		initialWidth: INITIAL_WIDTH,
		diff: 0,
	});

	const handlePointerDown = (e: PointerEvent) => {
		e.preventDefault();
		const { clientX, pointerId } = e;

		if (toggleElement.current) {
			toggleElement.current.setPointerCapture(pointerId);
		}

		resizeData.current.isResizing = true;
		resizeData.current.startX = clientX;
	};

	const handlePointerMove = ({ clientX }: PointerEvent) => {
		const { isResizing, startX, initialWidth } = resizeData.current;

		if (!isResizing) {
			return;
		}

		if (!isOpened) {
			dispatch(toggleThumbnails());
		}

		const diff = clientX - startX;
		resizeData.current.diff = diff;

		if (listElement.current) {
			const isClosed = startX < initialWidth;
			// if thumbnails column was previously closed its width should be set to MIN_WIDTH to prevent a visual bug when moving a pointer to right
			// resizeData.initialWidth can't be set to MIN_WIDTH in handlePointerUp because it won't allow to open hidden thubmnails by click on toggle
			const correctedInitialWidth = isClosed
				? MIN_WIDTH
				: initialWidth;
			const newWidth = correctedInitialWidth + diff;
			listElement.current.style.width = `${newWidth}px`;
		}
	};

	const handlePointerUp = () => {
		const { diff, initialWidth } = resizeData.current;
		const isClick = Math.abs(diff) <= 2;
		const shouldHideThumbnails = isOpened &&
			initialWidth + diff <= WIDTH_TO_HIDE &&
			diff < 0;

		if (isClick || shouldHideThumbnails) {
			const newWidth = isOpened ? MIN_WIDTH : initialWidth;
			dispatch(toggleThumbnails());
			// is needed to update Spread sizes
			dispatch(setThumbnailsWidth(newWidth));
			listElement.current!.style.width = `${newWidth}px`;
		} else {
			resizeData.current.initialWidth = listElement.current!.clientWidth;
			// is needed to update Spread sizes
			dispatch(setThumbnailsWidth(listElement.current!.clientWidth));
		}

		resizeData.current.isResizing = false;
		resizeData.current.startX = 0;
		resizeData.current.diff = 0;
	};

	return (
		<Wrapper
			type="button"
			ref={toggleElement}
			onPointerDown={handlePointerDown}
			onPointerMove={handlePointerMove}
			onPointerUp={handlePointerUp}
			// is needed to prevent unwanted clicks on thumbnail item when close thumbnails and on spread content when open thumbnails
			onTouchEnd={(e: PointerEvent) => e.preventDefault()}
			onDragStart={() => false}
		>
			<Inner>
				<Icon isOpened={isOpened}>
					{Utils.getSvgIcon('iconset_triangle-2')}
				</Icon>
			</Inner>
		</Wrapper>
	);
};

export default Toggle;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Editor/Thumbnails/Column/Toggle/index.tsx